import React, { useState, useEffect } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Link2, Play } from 'lucide-react';
import { videos, getVideo } from './videoCatalog';
import { videoPosterUrl } from '../lib/cloudinary';
import { VideoPlayer } from './VideoPlayer';
import { Footer } from '../components/landing/Footer';
import { BriefingModal } from '../components/landing/BriefingModal';

// Video library (route: /video) and single-video page (route: /video/:slug).
// Unknown slugs redirect to the library. Videos stream from Cloudinary's CDN.
export default function VideoPage() {
  const { slug } = useParams();
  const video = getVideo(slug);
  const [modalOpen, setModalOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = video ? `${video.title} — Video | Frontier` : 'Videos | Frontier';
    window.scrollTo(0, 0);
    setCopied(false);
  }, [video]);

  useEffect(() => {
    document.body.style.overflow = modalOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [modalOpen]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (e.g. insecure context) — nothing to do.
    }
  };

  if (slug && !video) return <Navigate to="/video" replace />;

  const related = videos.filter((v) => v.slug !== slug);

  return (
    <div className="min-h-screen bg-[#FBFBFB] overflow-x-clip">
      <div className="bg-[#090E1A] text-white">
        {/* Header */}
        <header className="px-6 md:px-10 lg:px-16 border-b border-white/10">
          <div className="w-full max-w-[1400px] mx-auto flex items-center justify-between h-16">
            <Link to="/" className="flex flex-col leading-none hover:opacity-80 transition">
              <span className="font-extrabold tracking-[0.28em] text-white text-base uppercase">FRONTIER</span>
              <span className="text-[9px] font-mono text-[#F58220] font-semibold uppercase tracking-widest mt-1 whitespace-nowrap">Deal OS™ · Operating System</span>
            </Link>
            <div className="flex items-center gap-3 sm:gap-6">
              <Link to="/products" className="hidden sm:inline-flex items-center gap-1.5 text-[13px] font-medium text-white/70 hover:text-white transition">
                <ArrowLeft size={14} /> Explore Deal OS™
              </Link>
              <button
                onClick={() => setModalOpen(true)}
                className="inline-flex items-center gap-1.5 whitespace-nowrap text-[13px] font-semibold bg-[#F58220] hover:bg-[#e07316] text-white px-4 py-2 rounded-full transition"
              >
                <span className="hidden sm:inline">Request a</span> Briefing <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </header>

        {video ? (
          <section className="px-6 md:px-10 lg:px-16 pt-10 sm:pt-14 pb-16 sm:pb-20">
            <div className="w-full max-w-[1200px] mx-auto">
              <div className="text-[11px] font-mono font-semibold uppercase tracking-[0.16em] text-[#F58220] mb-4">
                {video.eyebrow}
              </div>

              <VideoPlayer key={video.slug} video={video} />

              <div className="mt-8 sm:mt-10 grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12">
                <div className="lg:col-span-8">
                  <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight leading-[1.15]">{video.title}</h1>
                  <p className="mt-4 text-base sm:text-lg text-white/65 leading-relaxed">{video.description}</p>
                </div>
                <div className="lg:col-span-4 flex flex-col gap-5 lg:items-end">
                  <div className="flex flex-wrap gap-2 lg:justify-end">
                    {video.tags.map((t) => (
                      <span key={t} className="text-[11px] font-mono font-semibold text-white/70 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full">{t}</span>
                    ))}
                  </div>
                  <button
                    onClick={copyLink}
                    className="inline-flex items-center gap-1.5 text-[13px] font-medium text-white/70 hover:text-white border border-white/15 hover:border-white/30 px-4 py-2 rounded-full transition"
                  >
                    {copied ? <Check size={14} className="text-[#F58220]" /> : <Link2 size={14} />}
                    {copied ? 'Link copied' : 'Copy link'}
                  </button>
                </div>
              </div>
            </div>
          </section>
        ) : (
          <section className="px-6 md:px-10 lg:px-16 py-24 sm:py-32">
            <div className="w-full max-w-[1200px] mx-auto">
              <div className="text-[11px] font-mono font-semibold uppercase tracking-[0.16em] text-[#F58220] mb-4">Deal OS™ · Video Library</div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.1]">See Deal OS™ in action.</h1>
              <p className="mt-4 max-w-2xl text-white/65 text-base sm:text-lg leading-relaxed">
                Product walkthroughs and overviews of the Sovereign AI Platform for strategy and high-stakes deals.
              </p>
            </div>
          </section>
        )}
      </div>

      {related.length > 0 && (
        <section className="px-6 md:px-10 lg:px-16 py-16 sm:py-20 border-b border-slate-200/80">
          <div className="w-full max-w-[1200px] mx-auto">
            <h2 className="text-[11px] font-mono font-bold uppercase tracking-[0.16em] text-slate-900 mb-6">
              {video ? 'More videos' : 'All videos'}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map((v) => (
                <Link key={v.slug} to={`/video/${v.slug}`} className="group block">
                  <div className="relative rounded-xl overflow-hidden border border-slate-200 bg-[#090E1A] aspect-video">
                    <img
                      src={videoPosterUrl(v, 800)}
                      alt={v.title}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.02] transition duration-300"
                    />
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="w-12 h-12 rounded-full bg-white/90 group-hover:bg-[#F58220] flex items-center justify-center shadow-lg transition">
                        <Play size={18} className="text-slate-900 group-hover:text-white ml-0.5 transition" fill="currentColor" />
                      </span>
                    </span>
                  </div>
                  <div className="mt-3 text-[10px] font-mono font-semibold uppercase tracking-widest text-[#F58220]">{v.eyebrow}</div>
                  <div className="mt-1 text-[15px] font-semibold text-slate-900 group-hover:text-[#F58220] transition">{v.title}</div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer onOpenBriefing={() => setModalOpen(true)} />
      <BriefingModal isOpen={modalOpen} onClose={() => setModalOpen(false)} inquiryType="" />
    </div>
  );
}
